import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import PageHeader from '../components/PageHeader';
import { toast } from 'react-hot-toast';
import { Bell, Moon, Shield, AlertCircle, Save, Settings } from 'lucide-react';
import { Switch } from '../components/ui/switch';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import api from '../config/api';

const UserSettings = () => {
  const { token, logout } = useAuth();
  // 从主题上下文获取所有设置
  const { 
    isDarkMode, 
    isCompactMode,
    browserEnabled,
    updatesEnabled,
    updateTheme,
    updateNotifications,
    sendTestNotification
  } = useTheme();
  
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  
  // 使用上下文中的值初始化本地状态
  const [settings, setSettings] = useState({
    notifications: {
      email: true,
      browser: browserEnabled || true,
      updates: updatesEnabled || false,
    },
    appearance: {
      darkMode: isDarkMode || false,
      compactMode: isCompactMode || false,
    },
    privacy: {
      twoFactorAuth: false,
      saveHistory: true,
    },
  });

  // 修改获取设置的函数
  const loadUserSettings = async () => {
    if (!token) {
      toast.error('您需要登录才能访问此页面');
      if (logout) logout();
      navigate('/login');
      return;
    }

    setIsLoading(true);
    try {
      // console.log('正在从API获取用户设置...');
      const response = await api.get('/api/user/settings');
      // console.log('用户设置响应:', response.data);
      
      if (response.data.success && response.data.settings) {
        // 确保设置对象的结构是完整的，避免undefined错误
        const apiSettings = response.data.settings;
        setSettings({
          notifications: {
            email: apiSettings.notifications?.email ?? true,
            browser: apiSettings.notifications?.browser ?? true,
            updates: apiSettings.notifications?.updates ?? false,
          },
          appearance: {
            darkMode: apiSettings.appearance?.darkMode ?? false,
            compactMode: apiSettings.appearance?.compactMode ?? false,
          },
          privacy: {
            twoFactorAuth: apiSettings.privacy?.twoFactorAuth ?? false,
            saveHistory: apiSettings.privacy?.saveHistory ?? true,
          },
        });
      } else {
        // console.warn('API返回的设置格式无效');
        // 使用默认值
      }
    } catch (error) {
      // console.error('获取用户设置失败:', error);
      // 使用全局状态的值
      setSettings({
        notifications: {
          email: true,
          browser: browserEnabled || true,
          updates: updatesEnabled || false,
        },
        appearance: {
          darkMode: isDarkMode || false,
          compactMode: isCompactMode || false,
        },
        privacy: {
          twoFactorAuth: false,
          saveHistory: true,
        },
      });
      
      // 检查是否是认证错误
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        toast.error('登录状态已过期，请重新登录');
        if (logout) logout();
        navigate('/login');
      } else {
        toast.error('获取用户设置失败: ' + (error.message || '未知错误'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  // 组件挂载时加载设置
  useEffect(() => {
    loadUserSettings();
  }, [token]);

  // 修改 handleToggle 函数以更新通知和主题
  const handleToggle = (category, setting) => {
    setSettings(prevSettings => {
      const newSettings = {
        ...prevSettings,
        [category]: {
          ...prevSettings[category],
          [setting]: !prevSettings[category][setting]
        }
      };
      
      // 同步到全局设置状态
      if (category === 'appearance') {
        if (setting === 'darkMode') {
          updateTheme({ isDarkMode: newSettings.appearance.darkMode });
        } else if (setting === 'compactMode') {
          updateTheme({ isCompactMode: newSettings.appearance.compactMode });
        }
      }
      
      // 如果是通知设置，更新通知状态
      if (category === 'notifications') {
        if (setting === 'browser') {
          updateNotifications({ browserEnabled: newSettings.notifications.browser });
          
          // 如果启用了浏览器通知，请求权限
          if (newSettings.notifications.browser) {
            requestNotificationPermission();
          }
        } else if (setting === 'updates') {
          updateNotifications({ updatesEnabled: newSettings.notifications.updates });
        }
      }
      
      return newSettings;
    });
  };

  // 请求通知权限
  const requestNotificationPermission = async () => {
    if (!('Notification' in window)) {
      toast.error('您的浏览器不支持通知功能');
      return;
    }
    
    if (Notification.permission === 'granted') {
      toast.success('通知权限已授予');
      return;
    }
    
    if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        toast.success('通知权限已授予');
      } else {
        toast.error('通知权限被拒绝，请在浏览器设置中启用');
      }
    } else {
      toast.error('通知权限已被拒绝，请在浏览器设置中更改');
    }
  };

  // 保存设置
  const handleSave = async () => {
    if (!token) {
      toast.error('登录状态已失效，请重新登录');
      if (logout) logout();
      navigate('/login');
      return;
    }
    
    setIsLoading(true);
    try {
      // console.log('正在保存用户设置...');
      // console.log('要保存的设置:', settings);
      
      const response = await api.put('/api/user/settings', settings);
      
      if (response.data.success) {
        toast.success('设置已保存');
        
        // 更新全局设置状态
        updateTheme({
          isDarkMode: settings.appearance.darkMode,
          isCompactMode: settings.appearance.compactMode
        });
        
        updateNotifications({
          browserEnabled: settings.notifications.browser,
          updatesEnabled: settings.notifications.updates
        });
      } else {
        throw new Error(response.data.error || '保存失败');
      }
    } catch (error) {
      // console.error('保存设置失败:', error);
      
      // 检查是否是认证错误
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        toast.error('登录状态已过期，请重新登录');
        if (logout) logout();
        navigate('/login');
      } else {
        toast.error('保存设置失败: ' + (error.response?.data?.error || error.message || '未知错误'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  // 测试通知
  const handleTestNotification = async (type) => {
    try {
      if (type === 'browser') {
        if (!settings.notifications.browser) {
          toast.error('请先启用浏览器通知');
          return;
        }
        
        // 检查浏览器通知权限
        if (!('Notification' in window)) {
          toast.error('您的浏览器不支持通知功能');
          return;
        }
        
        // 如果没有授予权限，请求权限
        if (Notification.permission === 'default') {
          const permission = await Notification.requestPermission();
          if (permission !== 'granted') {
            toast.error('需要通知权限才能发送通知');
            return;
          }
        }
        
        // 显示测试通知
        await sendTestNotification('browser');
        
        // 仅当操作成功但没有抛出错误时显示成功消息
        if (Notification.permission === 'granted') {
          toast.success('已发送测试通知');
        }
      } else if (type === 'updates') {
        if (!settings.notifications.updates) {
          toast.error('请先启用产品更新通知');
          return;
        }
        await sendTestNotification('update');
        toast.success('已发送测试更新通知');
      }
    } catch (error) {
      // console.error(`测试${type}通知失败:`, error);
      toast.error(`测试${type}通知失败: ${error.message || '未知错误'}`);
    }
  };

  const SettingRow = ({ title, description, checked, onCheckedChange, testAction }) => (
    <div className="settings-row">
      <div className="settings-row__copy"><strong>{title}</strong><span>{description}</span></div>
      <div className="settings-row__actions">
        {testAction && <Button variant="outline" size="sm" onClick={testAction} disabled={isLoading || !checked}>测试</Button>}
        <Switch checked={checked} onCheckedChange={onCheckedChange} disabled={isLoading} aria-label={title} />
      </div>
    </div>
  );

  return (
    <div className="page-stack settings-page">
      <PageHeader eyebrow="PREFERENCES" title="账号设置" description="管理通知、界面外观与账户安全偏好。" icon={Settings} badge="个人配置" />
      <div className="settings-stack">
        <Card className="settings-card settings-card--notifications">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Bell className="size-5 text-accent" />通知设置</CardTitle>
            <CardDescription>选择系统可以通过哪些渠道向你发送运行与更新信息。</CardDescription>
          </CardHeader>
          <CardContent>
            <SettingRow title="电子邮件通知" description="接收许可证到期和重要更新邮件" checked={settings.notifications.email} onCheckedChange={() => handleToggle('notifications', 'email')} />
            <SettingRow title="浏览器通知" description="在当前设备显示实时系统通知" checked={settings.notifications.browser} onCheckedChange={() => handleToggle('notifications', 'browser')} testAction={() => handleTestNotification('browser')} />
            <SettingRow title="产品更新通知" description="接收新版本与功能变化提醒" checked={settings.notifications.updates} onCheckedChange={() => handleToggle('notifications', 'updates')} testAction={() => handleTestNotification('updates')} />
            {'Notification' in window && Notification.permission === 'denied' && (
              <Alert variant="destructive" className="mt-4">
                <AlertCircle /><AlertTitle>浏览器通知权限已被拒绝</AlertTitle>
                <AlertDescription>请在浏览器站点设置中允许此页面发送通知。</AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        <div className="settings-secondary-grid">
          <Card className="settings-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Moon className="size-5 text-accent" />外观设置</CardTitle>
              <CardDescription>调整控制台的显示密度与明暗模式。</CardDescription>
            </CardHeader>
            <CardContent>
              <SettingRow title="深色模式" description="启用低亮度石墨主题" checked={settings.appearance.darkMode} onCheckedChange={() => handleToggle('appearance', 'darkMode')} />
              <SettingRow title="紧凑模式" description="压缩内容间距以展示更多信息" checked={settings.appearance.compactMode} onCheckedChange={() => handleToggle('appearance', 'compactMode')} />
            </CardContent>
          </Card>

          <Card className="settings-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Shield className="size-5 text-accent" />隐私和安全</CardTitle>
              <CardDescription>控制账户保护和操作留痕策略。</CardDescription>
            </CardHeader>
            <CardContent>
              <SettingRow title="两步验证" description="为账户增加额外验证层" checked={settings.privacy.twoFactorAuth} onCheckedChange={() => handleToggle('privacy', 'twoFactorAuth')} />
              <SettingRow title="保存操作历史" description="记录当前账户的关键操作" checked={settings.privacy.saveHistory} onCheckedChange={() => handleToggle('privacy', 'saveHistory')} />
            </CardContent>
          </Card>
        </div>

        <div className="settings-save-bar">
          <span>更改会在保存后应用到当前账户。</span>
          <Button onClick={handleSave} disabled={isLoading}><Save data-icon="inline-start" />{isLoading ? '正在保存…' : '保存所有设置'}</Button>
        </div>
      </div>
    </div>
  );
};

export default UserSettings;
